import axios from "axios";
import { isPublicRoute } from "./utils/publicRoutes";
// const ServerURL = "https://reviewdevelopmentwebserviceosif.pboplus.com/api"
const ServerURL = process.env.REACT_APP_API_URL;
const OLAPServerURL = process.env.REACT_APP_OLAP_API_URL;

/**
 * Validate token before API call.
 * BUG-74: public-route bypass — if the current path is in the public-routes
 * list (vendor-signup, vendor-portal, etc.), don't force-redirect on missing
 * token; let the caller see a clean { data:null, error:... } response so the
 * page stays put. The forced redirect only fires on authenticated routes.
 * @returns {boolean} True if token exists, false otherwise
 */
const validateToken = () => {
  const token = localStorage.getItem("token");
  if (!token) {
    if (typeof window !== "undefined" && !isPublicRoute(window.location.pathname)) {
      window.location.href = '/';
    }
    return false;
  }
  return true;
};

const getData = async (url) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    let response = await fetch(`${ServerURL}/${url}`, {
      method: "GET",
      mode: "cors",
      headers: { "content-type": "application/json;charset=utf-8", Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (response.ok) {
      let result = await response.json();
      return { data: result, error: null };
    } else {
      let errorMsg = '';
      try {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const errorData = await response.json();
          errorMsg = errorData.error || errorData.message || JSON.stringify(errorData);
        } else {
          // Handle HTML error responses (like 404 pages)
          const textResponse = await response.text();
          if (textResponse.includes('<!DOCTYPE')) {
            errorMsg = `Server error: ${response.status} ${response.statusText}`;
          } else {
            errorMsg = textResponse || `Error: ${response.status} ${response.statusText}`;
          }
        }
      } catch (err) {
        console.error('Error parsing error response:', err);
        errorMsg = `Error fetching data: ${response.status} ${response.statusText}`;
      }
      return { data: null, error: { status: response.status, message: errorMsg } };
    }
  } catch (e) {
    return { data: null, error: { message: e.message } };
  }
};

const getFile = async (url) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    let response = await fetch(`${ServerURL}/${url}`, {
      method: "GET",
      mode: "cors",
      headers: { "content-type": "application/json;charset=utf-8", Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (response.ok) {
      return { data: response, error: null };
    } else {
      let errorMsg = '';
      try {
        const errorData = await response.json();
        errorMsg = errorData.error || errorData.message || JSON.stringify(errorData);
      } catch (err) {
        console.error('Error parsing error response:', err);
        errorMsg = `Error fetching file: ${response.status} ${response.statusText}`;
      }
      return { data: null, error: { message: errorMsg } };
    }
  } catch (error) {
    return { data: null, error: { message: error.message } };
  }
};

const postData = async (url, body) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    let response = await axios.post(`${ServerURL}/${url}`, body, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (response.status === 200 || response.status === 201) {
      let result = await response.data;
      return { data: result, error: null };
    } else {
      let errorMsg = '';
      if (response.data) {
        errorMsg = response.data.error || response.data.message || JSON.stringify(response.data);
      } else {
        errorMsg = `Error updating data: ${response.status} ${response.statusText}`;
      }
      return { data: null, error: { message: errorMsg } };
    }
  } catch (e) {
    if (e?.response?.data) {
      let errorMsg = e.response.data.error || e.response.data.message || JSON.stringify(e.response.data) || e.message;
      return { data: null, error: { message: errorMsg } };
    } else {
      return { data: null, error: { message: e.message } };
    }
  }
};

const postFile = async (url, formData) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    const response = await fetch(`${ServerURL}/${url}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem("token")}`
      },
      body: formData
    });

    // Check content type before parsing
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      // Server returned non-JSON (likely HTML error page)
      const text = await response.text();
      console.error('Non-JSON response received:', text.substring(0, 200));
      return { 
        error: { 
          message: `Server error: ${response.status} ${response.statusText}. Expected JSON but received ${contentType || 'unknown type'}` 
        }, 
        data: null 
      };
    }

    if (!response.ok) {
      let errorMsg = null;
      if (Array.isArray(data?.error)) {
        errorMsg = data.error;
      } else if (Array.isArray(data)) {
        errorMsg = data;
      } else {
        errorMsg = data.error || data.message || JSON.stringify(data) || 'File upload failed';
      }
      return { error: { message: errorMsg }, data: null };
    }
    return { data, error: null };
  } catch (error) {
    return { error: { message: error.message }, data: null };
  }
};

const downloadFile = async (url, body) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  console.log('Downloading file from URL:', ServerURL + '/' + url);
  try {
    const response = await fetch(`${ServerURL}/${url}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem("token")}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMsg = 'File download failed';
      try {
        const text = await response.text();
        try {
          const json = JSON.parse(text);
          errorMsg = json.message || json.error || text;
        } catch {
          errorMsg = text;
        }
      } catch { }
      return { error: { message: errorMsg }, data: null };
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download';
    if (contentDisposition) {
      const filenameRegex = /filename=([^;]+)/i;
      const match = filenameRegex.exec(contentDisposition);
      if (match?.[1]) {
        filename = match[1].trim().replace(/['"]/g, '');
      }
    }

    const data = await response.blob();
    return { data: data, filename: filename, error: null };
  } catch (error) {
    return { error: { message: error.message }, data: null };
  }
};

const putData = async (url, body) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    let response = await axios.put(`${ServerURL}/${url}`, body, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (response.status === 200 || response.status === 204 || response.status === 201) {
      let result = await response.data;
      return { data: result, error: null };
    } else if (response.status === 500) {
      return { data: null, error: { message: 'Internal Server Error' } };
    } else {
      let errorMsg = '';
      if (response.data) {
        errorMsg = response.data.error || response.data.message || JSON.stringify(response.data);
      } else {
        errorMsg = `Error updating data: ${response.status} ${response.statusText}`;
      }
      return { data: null, error: { message: errorMsg } };
    }
  } catch (e) {
    // Check if this is actually a successful response with message in error field
    if (e?.response?.status === 200 && e?.response?.data?.message && !e?.response?.data?.error) {
      return { data: e.response.data, error: null };
    }
    // Improved error handling for better visibility
    if (e?.response?.data) {
      let errorMsg = e.response.data.error || e.response.data.message || JSON.stringify(e.response.data) || e.message;
      return { data: null, error: { message: errorMsg } };
    } else {
      return { data: null, error: { message: e.message } };
    }
  }
};

const putFile = async (url, formData) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    console.log('putFile - Making PUT request to:', `${ServerURL}/${url}`);
    const response = await fetch(`${ServerURL}/${url}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem("token")}`
      },
      body: formData
    });
    
    console.log('putFile - Response status:', response.status);
    
    // Check content type before parsing
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('putFile - Response data:', data);
    } else {
      // Server returned non-JSON (likely HTML error page)
      const text = await response.text();
      console.error('putFile - Non-JSON response received:', text.substring(0, 200));
      return { 
        error: { 
          message: `Server error: ${response.status} ${response.statusText}. Expected JSON but received ${contentType || 'unknown type'}` 
        }, 
        data: null 
      };
    }
    
    if (!response.ok) {
      let errorMsg = data.error || data.message || JSON.stringify(data) || 'File update failed';
      return { error: { message: errorMsg }, data: null };
    }
    return { data, error: null };
  } catch (error) {
    console.error('putFile - Error:', error);
    return { error: { message: error.message }, data: null };
  }
};

const deleteData = async (url) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    let response = await axios.delete(`${ServerURL}/${url}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (response.status === 200 || response.status === 204) {
      let result = await response.data;
      return { data: result, error: null };
    } else {
      let errorMsg = '';
      if (response.data) {
        errorMsg = response.data.error || response.data.message || JSON.stringify(response.data);
      } else {
        errorMsg = `Error deleting data: ${response.status} ${response.statusText}`;
      }
      return { data: null, error: { message: errorMsg } };
    }
  } catch (e) {
    if (e?.response?.data) {
      let errorMsg = e.response.data.error || e.response.data.message || JSON.stringify(e.response.data) || e.message;
      return { data: null, error: { message: errorMsg } };
    } else {
      return { data: null, error: { message: e.message } };
    }
  }
};




// UPDATED: deleteData now accepts an optional 'body' argument
const deleteDatawithBody = async (url, body) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    const config = {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    };

    // If body exists, add it to config.data (Required format for axios.delete payloads)
    if (body) {
      config.data = body;
    }

    let response = await axios.delete(`${ServerURL}/${url}`, config);

    if (response.status === 200 || response.status === 204) {
      let result = await response.data;
      return { data: result, error: null };
    } else {
      let errorMsg = '';
      if (response.data) {
        errorMsg = response.data.error || response.data.message || JSON.stringify(response.data);
      } else {
        errorMsg = `Error deleting data: ${response.status} ${response.statusText}`;
      }
      return { data: null, error: { message: errorMsg } };
    }
  } catch (e) {
    if (e?.response?.data) {
      let errorMsg = e.response.data.error || e.response.data.message || JSON.stringify(e.response.data) || e.message;
      return { data: null, error: { message: errorMsg } };
    } else {
      return { data: null, error: { message: e.message } };
    }
  }
};




const postReport = async (url, body) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    let response = await axios.post(`${OLAPServerURL}/${url}`, body, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (response.status === 200 || response.status === 201) {
      let result = await response.data;
      return { data: result, error: null };
    } else {
      let errorMsg = '';
      if (response.data) {
        errorMsg = response.data.error || response.data.message || JSON.stringify(response.data);
      } else {
        errorMsg = `Error updating data: ${response.status} ${response.statusText}`;
      }
      return { data: null, error: { message: errorMsg } };
    }
  } catch (e) {
    if (e?.response?.data) {
      let errorMsg = e.response.data.error || e.response.data.message || JSON.stringify(e.response.data) || e.message;
      return { data: null, error: { message: errorMsg } };
    } else {
      return { data: null, error: { message: e.message } };
    }
  }
};




const downloadReport = async (url, body) => {
  // Validate token before making request
  if (!validateToken()) {
    return { data: null, error: { message: 'Authentication required. Please login.' } };
  }

  try {
    const response = await fetch(`${OLAPServerURL}/${url}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem("token")}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMsg = 'File download failed';
      try {
        const text = await response.text();
        try {
          const json = JSON.parse(text);
          errorMsg = json.message || json.error || text;
        } catch {
          errorMsg = text;
        }
      } catch { }
      return { error: { message: errorMsg }, data: null };
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download';
    if (contentDisposition) {
      const filenameRegex = /filename=([^;]+)/i;
      const match = filenameRegex.exec(contentDisposition);
      if (match?.[1]) {
        filename = match[1].trim().replace(/['"]/g, '');
      }
    }

    const data = await response.blob();
    return { data: data, filename: filename, error: null };
  } catch (error) {
    return { error: { message: error.message }, data: null };
  }
};

export { getData, postData, postFile, getFile, putData, putFile, deleteData, deleteDatawithBody, downloadFile, downloadReport, ServerURL, postReport };

